/*
 * Copyright (c) 2023 SHYFTBASE INC.
 * Author: SHYFTBASE INC.
 */
import { initializeApp, getApp } from 'firebase/app'
import { collection, query, where, getDocs, limit, orderBy, initializeFirestore, getCountFromServer, getFirestore } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getStorage, ref } from 'firebase/storage'

// Models for each collection
import {
    ActivityLog,
    Order,
    OrderCount,
    Calendar,
    Notification,
    Invoice,
    Client,
    Contractors,
    DISTRIBUTIONZONES,
    ReportSetting,
    ContactlessDelivery,
    IkeaContractors,
    TMSFields,
    Collection,
    TemplateHeader,
    RateConditions,
    Zones,
    Views,
} from './Model'

const config = {
    apiKey: process.env.REACT_APP_API_KEY || '',
    authDomain: process.env.REACT_APP_AUTH_DOMAIN || '',
    databaseURL: process.env.REACT_APP_DATABASE_URL || '',
    projectId: process.env.REACT_APP_PROJECT_ID || '',
    appId: process.env.REACT_APP_APP_ID || '',
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET || '',
}

const app = initializeApp(config)

export const fs = initializeFirestore(getApp(), {ignoreUndefinedProperties: true})
export const firestore = initializeFirestore(app, {ignoreUndefinedProperties: true})

export const auth = getAuth()
// auth.languageCode = localStorage ?  localStorage?.getItem('prefLang') || 'en' : 'en'

export const db = getFirestore(app)

export const storage = getStorage(getApp())
export const tem_storage = getStorage(getApp(), `${process.env.REACT_APP_TEMP_STORAGE}`)
export const attachments_storage = getStorage(getApp(), `${process.env.REACT_APP_ATTACHMENTS_STORAGE}`)
export const bols_storage= getStorage(getApp(), `${process.env.REACT_APP_BOLS_STORAGE}`)
export const gsReference_template = ref(storage, `${process.env.REACT_APP_FSC_TEMPLATE}`);

export const fb = getApp()
export const functions = getFunctions(getApp())

export const getProjectCollection = (name: string) => {
    return collection(firestore, name)
}

export {
    collection, query, where, getDocs, orderBy, limit
}
/* END OF CLEAN VERSION */

export const activityLogCollection = collection(fs, 'ACTIVITY_LOGS_MAIN_PORTAL')

export const activityLog = ActivityLog(activityLogCollection)

export const validateEmail = httpsCallable(functions, 'validateEmail')
export const getAvailabilities = httpsCallable(functions, 'getAvailabilities')

export const createOrder = httpsCallable(functions, 'createOrder')
export const updateOrder = httpsCallable(functions, 'updateOrder')
export const cancelOrder = httpsCallable(functions, 'cancelOrder')
export const requestInfo = httpsCallable(functions, 'requestInfo')
export const approveOrder = httpsCallable(functions, 'approveOrder')
export const fetchHistory = httpsCallable(functions, 'fetchHistory')
export const rescheduleOrderDelivery = httpsCallable(functions, 'TRACKING-rescheduleOrderDelivery')
export const handlePendingOrder = httpsCallable(functions, 'TRACKING-handlePendingOrder')
export const saveTemplateHeader = httpsCallable(functions, 'SAVE_TEMPLATE_HEADER-handler')

/* MAIN PORTAL COLLECTION */
export const activitiesCollectionInstance = collection(fs, 'ACTIVITY_LOGS')

/* TBD: We need to streamline the data Model. We have lots of duplicate/redundant code block in each Model */
export const ordersCollectionInstance = collection(fs, 'TMS_ORDERS')
export const ordersCollection = Order(ordersCollectionInstance)

export const rateTableTMSCollectionInstance = collection(firestore, 'RATE_TABLES_LIVE_V2')
export const rateTableTMSCollection = Order(rateTableTMSCollectionInstance)

export const templateHeaderCollectionInstance = collection(fs, 'IMPORT_TEMPLATES')
export const templateHeaderCollection = TemplateHeader(templateHeaderCollectionInstance)

export const bulkErrorCollectionInstance = collection(fs, 'BULK_IMPORT_ERRORS')
export const bulkErrorCollection = TemplateHeader(bulkErrorCollectionInstance)

export const ordersCountCollectionInstance = collection(fs, 'ORDERS_COUNT')
export const ordersCountCollection = OrderCount(ordersCountCollectionInstance)

export const calendarCollectionInstance = collection(fs, 'CALENDAR')
export const calendarCollection = Calendar(calendarCollectionInstance)

export const invoiceCollectionInstance = collection(fs, 'INVOICE_MANAGEMENT')
export const invoiceCollection = Invoice(invoiceCollectionInstance)

export const rateConditionsLiveCollection = collection(firestore, 'RATE_TABLES_CONDITIONS')
export const rateConditionsCollection = RateConditions(rateConditionsLiveCollection)

export const rateServicesLiveCollection = collection(firestore, 'RATE_TABLES_SERVICES')
export const rateServicesCollection = RateConditions(rateServicesLiveCollection)

export const rateDedicatedServicesLiveCollection = collection(firestore, 'GLOBALDEF_DEDICATED_SERVICES')
export const rateDedicatedServicesCollection = RateConditions(rateDedicatedServicesLiveCollection)

export const dedicatedStemsLiveCollection = collection(firestore, 'DEDICATED_STEMS')
export const dedicatedStemsCollection = RateConditions(dedicatedStemsLiveCollection)

export const optionTypesLiveCollection = collection(firestore, 'GLOBALDEF_ORDERTYPES')
export const optionTypesCollection = RateConditions(optionTypesLiveCollection)

export const substatsLiveCollection = collection(firestore, 'GLOBALDEF_SUBSTATUSES')
export const substatsCollection = RateConditions(substatsLiveCollection)

export const ratezonesLiveCollection = collection(firestore, 'RATE_TABLES_ZONES')
export const ratezonesCollection = RateConditions(ratezonesLiveCollection)

export const regionsDefLiveCollection = collection(firestore, 'REGIONS')
export const regionsDefCollection = Order(regionsDefLiveCollection)

export const fscratesLiveCollection = collection(firestore, 'RATE_TABLES_FSC_RATES')
export const fscratesCollection = RateConditions(fscratesLiveCollection)

export const dedicatedTiersLiveCollection = collection(firestore, 'RATE_TABLES_DEDICATED_TIERS')
export const dedicatedTiersCollection = RateConditions(dedicatedTiersLiveCollection)

export const escrowTypesLiveCollection = collection(firestore, 'RATE_TABLES_ESCROW_TYPES')
export const escrowTypesCollection = RateConditions(escrowTypesLiveCollection)

export const clientsCollectionInstance = collection(firestore, 'CLIENTS')
export const clientsCollection = Client(clientsCollectionInstance)

export const allContractorsCollectionInstance = collection(firestore, `${process.env.REACT_APP_COLLECTION_CONTRACTORS}`)
export const allContractorsCollection = Contractors(allContractorsCollectionInstance)

export const distributionZonesCollectionInstance = collection(firestore, `${process.env.REACT_APP_COLLECTION_DISTRIBUTIONZONES}`)
export const distributionZonesCollection = DISTRIBUTIONZONES(distributionZonesCollectionInstance)

export const reportsSettingCollectionInstance = collection(firestore, 'REPORTS_SETTING')
export const reportsSettingCollection = ReportSetting(reportsSettingCollectionInstance)

export const contactlessDeliveryCollectionInstance = collection(firestore, 'CONTACTLESS_DELIVERY_SETTING')
export const contactlessDeliveryCollection = ContactlessDelivery(contactlessDeliveryCollectionInstance)


export const ikeaContractorsCollectionInstance = collection(firestore, 'IKEA_CONTRACTORS')
export const ikeaContractors = IkeaContractors(ikeaContractorsCollectionInstance)

export const ikeaPostalCodeCollectionInstance = collection(firestore, 'IKEA_POSTALCODE_ZONES')
export const ikeaPostalCode = IkeaContractors(ikeaPostalCodeCollectionInstance)

export const tmsConfigurationFieldsCollectionInstance = collection(firestore, 'CONFIGURATIONS/TABLE/FIELDS')
export const tmsConfigurationFieldsCollection = TMSFields(tmsConfigurationFieldsCollectionInstance)

export const inventoryFieldsCollectionInstance = collection(firestore, 'CONFIGURATIONS/TABLE/INVENTORYFIELDS')
export const inventoryFieldsCollection = TMSFields(inventoryFieldsCollectionInstance)

export const OrdersCollectionInstance = collection(firestore, 'TMS_ORDERS')
export const OrdersCollection = Order(OrdersCollectionInstance)

export const BolsCollectionInstance = collection(firestore, 'BOLS')
export const BolsCollection = Order(BolsCollectionInstance)

export const tableViews = collection(firestore, 'TMS_VIEWS')
export const tableViewsCollection = Collection(tableViews)

export const vehiclesViews = collection(firestore, 'VEHICLES')
export const vehiclesCollection = Collection(vehiclesViews)

export const actions = collection(firestore, 'ACTIONS_NEW')
export const actionsCollection = Collection(actions)

export const roles = collection(firestore, 'ROLES_NEW')
export const rolesCollection = Collection(roles)

export const users = collection(firestore, `${process.env.REACT_APP_COLLECTION_USERS}`)
export const usersCollection = Collection(users)

export const fleet = collection(firestore, 'FLEET')
export const fleetCollection = Collection(fleet)

export const vendors = collection(firestore, 'VENDORS')
export const vendorsCollection = Collection(vendors)

export const contractors = collection(firestore, `CONTRACTORS`)
export const contractorsCollection = Collection(contractors)

export const notifications = collection(firestore, "NOTIFICATIONS")
export const notificationsCollection = Collection(notifications)

export const getOrdersCollection = (name: string) => {
    const orders = collection(firestore, name)
    return Order(orders)
}

export const getCollection = (name: string) => {
    const collectionRef = collection(firestore, name)
    return Collection(collectionRef)
}

export const getOrderSubCollection = (name: string, id: string, subName: string) => {
    const ref = collection(firestore, `${name}/${id}/${subName}`)
    return Collection(ref)
}

export const getOrderDocument = (id: string) => {
    return ordersCollection.doc(id)
}

export const getDistributionZoneCollection = (name: string) => {
    const distributionzone = collection(firestore, name)
    return Zones(distributionzone)
}

export const getContractorsCollection = (name: string) => {
    const distributionzone = collection(firestore, name)
    return Zones(distributionzone)
}

export const getViewsCollection = (name: string) => {
    const views = collection(firestore, name)
    return Views(views)
}

export const getCountForCollection = async (collection: any) => {

    const snapshot = await getCountFromServer(collection)
    return snapshot.data().count;
}

export const getCountForQuery = async (queryRef: any) => {
        const snapshot = await getCountFromServer(queryRef)
        return Promise.resolve(snapshot?.data()?.count || 0);
    }

/* DO WE NEED ANONYMOUS EXPORTS IN ADDITION TO THE NAMED EXPORTS ABOVE? */
export default {
    allContractorsCollection,
    auth,
    clientsCollection,
    contactlessDeliveryCollection,
    distributionZonesCollection,
    ikeaContractors,
    invoiceCollection,
    ordersCollection,
    ordersCountCollection,
    reportsSettingCollection,
    storage,
    templateHeaderCollection,
    gsReference_template
}
